<template>
  <div
    class="layout"
    v-loading="isLoading && !areModalsVisible"
  >
    <template v-if="site">
      <!-- Header -->
      <LayoutHeader
        class="layout__header"
        :title="site.title"
        :logo-url="site.logo.publicUrl"
      />
      <!-- Content -->
      <div
        class="layout__content"
        :key="`layout__content--${siteFetchTimestamp}`"
      >
        <!-- View -->
        <router-view
          :tabs="site.programTabs"
          :programs="site.programs.rows"
        />
        <!-- Footer -->
        <div class="layout__content__footer">
          <div class="layout__content__footer__wrapper">
            <i class="layout__content__footer__icon icon-info" />
            <p class="layout__content__footer__text">
              <span v-text="i18n('entities.inscription.footer.text')" />
              <a
                class="layout__content__footer__link"
                :href="`mailto:${i18n('entities.inscription.footer.email')}`"
                v-text="i18n('entities.inscription.footer.email')"
                target="_blank"
              />
            </p>
          </div>
        </div>
      </div>
    </template>

    <el-dialog
      v-for="dialog in dialogs"
      :key="`layout__dialog--${dialog.componentName}`"
      class="layout__dialog"
      :class="dialog.classes || {}"
      :visible="dialog.visible"
      destroy-on-close
      :close-on-click-modal="!lockDialog"
      :close-on-press-escape="!lockDialog"
      :show-close="!lockDialog"
      @close="dialog.onClose"
      :fullscreen="isMobileOrTablet"
    >
      <component
        :is="dialog.componentName"
        v-bind="dialog.props || {}"
      />
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import { i18n } from '@shared/i18n'

import { userAuthPromise } from '@/auth'
import config from '@/config'
import inscriptionSiteQuery from '@/graphql/queries/inscriptionSite'

import LayoutHeader from '@/components/LayoutHeader.vue'

import LoginModal from '@/components/LoginModal.vue'
import SignUpModal from '@/components/SignUpModal.vue'
import ForgotPasswordModal from '@/components/ForgotPasswordModal.vue'
import BetweenStepsModal from '@/components/BetweenStepsModal.vue'
import SuccessModal from '@/components/SuccessModal.vue'
import InscriptionDeleteModal from '@/components/InscriptionDeleteModal.vue'

export default {
  name: 'layout',
  data() {
    return {
      siteFetchTimestamp: +new Date,
      initializingUserAuth: true,
      i18n,
    }
  },
  created() {
    userAuthPromise.then(() => {
      this.initializingUserAuth = false
    })
  },
  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      dialogWidth: 'dialogWidth',
      loginModalVisible: 'loginModalVisible',
      signupModalVisible: 'signupModalVisible',
      forgotPasswordModalVisible: 'forgotPasswordModalVisible',
      betweenStepsModalVisible: 'betweenStepsModalVisible',
      inscriptionDeleteModalVisible: 'inscriptionDeleteModalVisible',
      successModalVisible: 'successModalVisible',
      successModalType: 'successModalType',
      applyFlowSuccessCallback: 'applyFlowSuccessCallback',
      dialogOnCloseCallback: 'dialogOnCloseCallback',
      lockDialog: 'lockDialog',
    }),
    areModalsVisible() {
      return this.loginModalVisible || this.signupModalVisible
    },
    waitingForUserAuth() {
      return !!userAuthPromise.done
    },
    isLoading() {
      return this.initializingUserAuth || this.$apollo.queries.site.loading
    },
    slug() {
      return this.$route.params.slug
    },
    dialogs() {
      return [{
        componentName: 'login-modal',
        visible: this.loginModalVisible,
        onClose: () => {
          this.setLoginModalVisible(false)
          this.onDialogClose()
        },
        props: {
          siteLoading: this.isLoading,
        },
      }, {
        componentName: 'sign-up-modal',
        visible: this.signupModalVisible,
        classes: 'layout__dialog--no-padding',
        onClose: () => {
          this.setSignupModalVisible(false)
          this.onDialogClose()
        },
        props: {
          siteLoading: this.isLoading,
        },
      }, {
        componentName: 'forgot-password-modal',
        visible: this.forgotPasswordModalVisible,
        onClose: () => {
          this.setForgotPasswordModalVisible(false)
          this.onDialogClose()
        },
      }, {
        componentName: 'between-steps-modal',
        visible: this.betweenStepsModalVisible,
        onClose: () => {
          this.setBetweenStepsModalVisible(false)
          this.onDialogClose()
        },
      }, {
        componentName: 'success-modal',
        visible: this.successModalVisible,
        onClose: () => {
          this.setSuccessModalVisible(false)
          this.onDialogClose()
        },
        props: {
          type: this.successModalType,
        },
      }, {
        componentName: 'inscription-delete-modal',
        visible: this.inscriptionDeleteModalVisible,
        onClose: () => {
          this.setInscriptionDeleteModalVisible(false)
          this.onDialogClose()
        },
      }]
    },
    isMobileOrTablet(){
      const tabletMaxWidth = 992
      return screen.width <= tabletMaxWidth
    },
  },
  mounted() {
    this.checkSlug()
    this.$root.$on('modal.open.login', this.onLoginModalOpen)
  },
  beforeDestroy() {
    this.$root.$off('modal.open.login', this.onLoginModalOpen)
  },
  methods: {
    ...mapMutations({
      setTitle: 'setTitle',
      setPropertyId: 'setPropertyId',
      setSubpropertyId: 'setSubpropertyId',
      setLoginModalVisible: 'setLoginModalVisible',
      setSignupModalVisible: 'setSignupModalVisible',
      setForgotPasswordModalVisible: 'setForgotPasswordModalVisible',
      setBetweenStepsModalVisible: 'setBetweenStepsModalVisible',
      setInscriptionDeleteModalVisible: 'setInscriptionDeleteModalVisible',
      setSuccessModalVisible: 'setSuccessModalVisible',
      setSuccessModalType: 'setSuccessModalType',
      setApplyFlowSuccessCallback: 'setApplyFlowSuccessCallback',
      setDialogOnCloseCallback: 'setDialogOnCloseCallback',
    }),
    checkSlug() {
      if (!this.slug) this.redirectToOwqloWebsite()
    },
    redirectToOwqloWebsite() {
      window.location.replace(config.owqloWebsiteUrl)
    },
    onLoginModalOpen(callback) {
      this.setLoginModalVisible(true)
      if (callback) this.setApplyFlowSuccessCallback(callback)
    },
    onDialogClose() {
      if (typeof this.dialogOnCloseCallback === 'function') this.dialogOnCloseCallback()
      this.setDialogOnCloseCallback(null)
    },
  },
  apollo: {
    site: {
      query: inscriptionSiteQuery,
      variables() {
        return {
          slug: this.slug,
        }
      },
      update(data) {
        return data.inscriptionSite
      },
      skip() {
        return !this.slug || this.initializingUserAuth
      },
      result() {
        const hasPrograms = this.site && 'programs' in this.site && 'rows' in this.site.programs && !!this.site.programs.rows.length
        if (!hasPrograms) return this.redirectToOwqloWebsite()

        document.title = `${this.site.title} - OWQLO`
        this.setTitle(this.site.title)
        this.setPropertyId(this.site.propertyId)
        this.setSubpropertyId(this.site.subpropertyId)

        this.siteFetchTimestamp = +new Date
      },
      error() {
        this.redirectToOwqloWebsite()
      },
    },
  },
  watch: {
    slug: 'checkSlug',
    async currentUser() {
      await this.$nextTick()
      this.$apollo.queries.site.refetch()
    },
  },
  components: {
    LayoutHeader,
    LoginModal,
    SignUpModal,
    ForgotPasswordModal,
    BetweenStepsModal,
    SuccessModal,
    InscriptionDeleteModal,
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css?family=Roboto:300,400,500,600,700&display=swap');

@import '~@shared/styles/theme/default.css';

@import '~@shared/styles/reset.css';
@import '~@shared/styles/layout.css';
@import '~@shared/styles/form.css';
@import '~@shared/styles/element-ui.css';
@import '~@shared/styles/icons.css';
@import '~@shared/styles/loading.css';

@import '~@/styles/transitions.scss';

:root {
  --header-height: 50px;
  --header-padding: 15px;
  --header-outer-height: calc(var(--header-height) + var(--header-padding) * 2);
}

.layout {
  --modal-width-desktop: 750px;
  --modal-margin-top-desktop: 10vh;
  
  --footer-height: 96px;
  --footer-margin-top: 32px;
  --footer-outer-height: calc(var(--footer-height) + var(--footer-margin-top));

  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh !important;
  height: 100vh;
  background-color: #f8f8f8;
  color: #404040;

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr;

  * {
    word-break: break-word;
  }

  &__content {
    max-width: 100vw;
    overflow: auto;
    overflow-x: hidden;
    background-color: #ffffff;

    & > * {
      max-width: 100vw;
      min-height: calc(100vh - var(--header-outer-height) - var(--footer-outer-height));
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: center;

      min-height: 0;
      height: var(--footer-height);
      background: #404040;
      margin-top: var(--footer-margin-top);

      &__wrapper {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: center;
        justify-items: center;
        grid-gap: 3px;

        @include mobile {
          grid-template-columns: auto 1fr;
          padding: 0 40px;
        }

        @include tablet {
          grid-template-columns: auto 1fr;
          padding: 0 40px;
        }
      }

      &__text,
      &__link {
        font-size: 12px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.5;
        letter-spacing: normal;
        color: #FFFFFF;
      }

      &__icon {
        font-size: 20px;
        color: #FFFFFF;
        margin: -2px 2px 0 0;

        @include mobile {
          font-size: 24px;
          align-self: start;
          margin: -4px 2px 0 0;
        }

        @include tablet {
          font-size: 24px;
          align-self: start;
          margin: -4px 2px 0 0;
        }
      }

      &__link {
        font-weight: bold;
        color: #2acccc;
        margin-left: 3px;
      }
    }
  }

  &__dialog {
    &--no-padding {
      .el-dialog {
        .el-dialog__body {
          padding: 50px 0 0;
        }
      }
    }

    .el-dialog__body {
      @include small-mobile {
        padding: 50px 32px;
      }
      @include mobile {
        padding-bottom: 0;
      }
      padding: 50px 64px 70px;
    }
  }

  .el-dialog__wrapper {
    .el-dialog {
      width: var(--modal-width-desktop);
      margin-top: var(--modal-margin-top-desktop) !important;

      @include mobile {
        width: 100vw;
        height: 100vh;
        margin: 0 !important;
      }

      @include tablet {
        width: 100vw;
        height: 100vh;
        margin: 0 !important;
      }
    }
  }
}
</style>
